import { collection, getDocs, query, where, limit } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db, app } from "./init";
import { convertTimestampToDate } from "./firestore";

/**
 * 顯示數據調整（App 顯示的播放 / 收藏 / 按讚數）
 *
 * 調整量直接加在 dramas / videos 的計數欄位上，累計調整量存在 display_adjustments/{dramaDocId}。
 *   真實值 = 欄位值 − 累計調整量
 * CMS 內部統計一律顯示真實值；只有「App 顯示數據」卡片顯示調整後的數字。
 */

export type DramaDisplayField = "drama_play_times" | "favorites_count";
export type VideoDisplayField = "likeCount";

export interface DisplayAdjustment {
  drama_doc_id: string;
  drama_title?: string;
  drama?: Partial<Record<DramaDisplayField, number>>;
  videos?: Record<string, { likeCount?: number; order?: number | null }>;
}

export interface DisplayMetricAuditLog {
  id: string;
  action: "set" | "reset";
  target_type: "drama" | "video";
  target_id: string;
  field: DramaDisplayField | VideoDisplayField;
  drama_doc_id: string;
  drama_title: string;
  episode_order: number | null;
  before_value: number;
  after_value: number;
  delta: number;
  real_value: number;
  cumulative_adjustment_after: number;
  reason: string;
  operator_email: string | null;
  created_at: Date | null;
}

export type DisplayAdjustmentMap = Map<string, DisplayAdjustment>;

/** 讀全部調整量（key = drama doc id）。無權限時回傳空 Map，頁面照常顯示欄位值 */
export async function getDisplayAdjustments(): Promise<DisplayAdjustmentMap> {
  try {
    const snap = await getDocs(collection(db, "display_adjustments"));
    return new Map(snap.docs.map((d) => [d.id, d.data() as DisplayAdjustment]));
  } catch (error) {
    console.warn("無法讀取 display_adjustments，統計將顯示欄位原值:", error);
    return new Map();
  }
}

export function dramaAdjustment(
  adjustments: DisplayAdjustmentMap | undefined,
  dramaDocId: string | undefined,
  field: DramaDisplayField
): number {
  if (!adjustments || !dramaDocId) return 0;
  return Number(adjustments.get(dramaDocId)?.drama?.[field] || 0);
}

export function videoLikeAdjustment(
  adjustments: DisplayAdjustmentMap | undefined,
  dramaDocId: string | undefined,
  videoId: string | undefined
): number {
  if (!adjustments || !dramaDocId || !videoId) return 0;
  return Number(adjustments.get(dramaDocId)?.videos?.[videoId]?.likeCount || 0);
}

/** 單一劇的稽核紀錄（新到舊，僅 admin 可讀） */
export async function getDisplayMetricAuditLogs(
  dramaDocId: string,
  max = 100
): Promise<DisplayMetricAuditLog[]> {
  const snap = await getDocs(
    query(
      collection(db, "display_metric_audit_logs"),
      where("drama_doc_id", "==", dramaDocId),
      limit(max)
    )
  );
  return snap.docs
    .map((d) => {
      const data = d.data();
      return {
        ...(data as Omit<DisplayMetricAuditLog, "id" | "created_at">),
        id: d.id,
        created_at: convertTimestampToDate(data.created_at) ?? null,
      };
    })
    .sort((a, b) => (b.created_at?.getTime() ?? 0) - (a.created_at?.getTime() ?? 0));
}

export interface AdjustDisplayMetricParams {
  target_type: "drama" | "video";
  target_id: string;
  field: DramaDisplayField | VideoDisplayField;
  /** 希望 App 顯示的數字；reset 時省略 */
  target_value?: number;
  reset?: boolean;
  /** 選填；留空仍會記錄操作者、時間及前後數值 */
  reason?: string;
}

export interface AdjustDisplayMetricResult {
  before_value: number;
  after_value: number;
  real_value: number;
  cumulative_adjustment: number;
  audit_id: string;
}

/** 呼叫 adjustDisplayMetric（Admin only），一次調整一個欄位 */
export async function adjustDisplayMetric(
  params: AdjustDisplayMetricParams
): Promise<AdjustDisplayMetricResult> {
  const user = getAuth(app).currentUser;
  if (!user) throw new Error("用戶未登入");
  const token = await user.getIdToken();

  const projectId =
    app.options.projectId ||
    import.meta.env.VITE_FIREBASE_PROJECT_ID ||
    "onreel-sandbox";
  const url = `https://asia-east1-${projectId}.cloudfunctions.net/adjustDisplayMetric`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}
